// src/components/PaymentDetailModal.jsx
import React from "react";
import PropTypes from "prop-types";
import { Modal, Row, Col, Card, Badge, Button } from "react-bootstrap";

// Optional fallbacks if you don't pass helpers from parent
const fallbackFormatCurrency = (amount, currency = "LKR") =>
  `${currency} ${Number(amount ?? 0).toLocaleString()}`;
const fallbackFormatDate = (d) =>
  new Date(d).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
const fallbackGetStatusVariant = (status = "") => {
  switch (String(status).toLowerCase()) {
    case "paid":
      return "success";
    case "pending":
      return "warning";
    case "failed":
      return "danger";
    default:
      return "secondary";
  }
};

const PaymentDetailModal = ({
  show,
  onHide,
  payment, // the selectedPayment object
  formatCurrency = fallbackFormatCurrency,
  formatDate = fallbackFormatDate,
  getStatusVariant = fallbackGetStatusVariant,
}) => {
  const finals = payment?.FinalAllocations ?? [];
  const pendings = payment?.PendingAllocations ?? [];

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Payment Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {payment && (
          <div>
            <Row className="mb-3">
              <Col md={6}>
                <strong className="text-muted small">Customer Code</strong>
                <div className="mb-2">{payment.CustomerCode}</div>
              </Col>
              <Col md={6}>
                <strong className="text-muted small">Customer ID</strong>
                <div className="mb-2">{payment.CusID}</div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <strong className="text-muted small">Order ID</strong>
                <div className="mb-2">
                  <code className="small d-block text-break">
                    {payment.OrderId}
                  </code>
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <strong className="text-muted small">Amount</strong>
                <div className="h5 fw-bold mb-2">
                  {formatCurrency(payment.TotalAmount, payment.Currency)}
                </div>
              </Col>
              <Col md={6}>
                <strong className="text-muted small">Status</strong>
                <div className="mb-2">
                  <Badge bg={getStatusVariant(payment.Status)} className="fs-6">
                    {payment.Status}
                  </Badge>
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <strong className="text-muted small">Transaction UUID</strong>
                <div className="mb-2">
                  <code className="small">{payment.ReqTransactionUuid}</code>
                </div>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={6}>
                <strong className="text-muted small">Created At</strong>
                <div className="mb-2">{formatDate(payment.CreatedAt)}</div>
              </Col>
              <Col md={6}>
                <strong className="text-muted small">Updated At</strong>
                <div className="mb-2">{formatDate(payment.UpdatedAt)}</div>
              </Col>
            </Row>

            {(finals.length > 0 || pendings.length > 0) && (
              <div>
                <h5 className="fw-bold mb-3">Allocations</h5>

                {finals.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-success mb-2">Final Allocations</h6>
                    {finals.map((a) => (
                      <Card key={a.Id} className="mb-2 border-success">
                        <Card.Body className="py-2 bg-light bg-opacity-25">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-bold">{a.AccountCode}</span>
                            <span className="fw-bold">
                              {formatCurrency(a.Amount)}
                            </span>
                          </div>
                          {a.CreatedAt && (
                            <small className="text-muted">
                              {formatDate(a.CreatedAt)}
                            </small>
                          )}
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                )}

                {pendings.length > 0 && (
                  <div>
                    <h6 className="text-warning mb-2">Pending Allocations</h6>
                    {pendings.map((a) => (
                      <Card key={a.Id} className="mb-2 border-warning">
                        <Card.Body className="py-2 bg-warning bg-opacity-10">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-bold">{a.AccountCode}</span>
                            <span className="fw-bold">
                              {formatCurrency(a.Amount)}
                            </span>
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

PaymentDetailModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  payment: PropTypes.object, // shape varies; keep flexible
  formatCurrency: PropTypes.func,
  formatDate: PropTypes.func,
  getStatusVariant: PropTypes.func,
};

export default React.memo(PaymentDetailModal);
